/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect } from 'react';
import {
  Box,
  Text,
  GridItem,
  Button,
  HStack,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import {
  Heading,
  FormTextInput,
  FormPasswordInput,
  strengthChecker,
} from '@laxmimanogna/code-quick-components';
import {
  MailIcon,
  ViewIcon,
  EyeHiddenIcon,
  EyeIcon,
  LockIcon,
  DownIcon,
  UpIcon,
} from '../../constants/IconData';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../constants/Routes';
import CQContainer from '../../components/layout/CQContainer';
import { AuthContext } from '../../providers/AuthProvider';
import { UserManagementContext } from '../../providers/UserManagementProvider';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AppColors from '../../constants/AppColors';
import { FONT_FAMILY } from '../../constants/Theme';
import { PasswordSrength } from './PasswordStrength';
import FormInputField from '../../components/form/form_text_input/FormTextInput';
import { toTitleCase } from '../../utils/common.utils';

const AccountForm = () => {

  const navigate = useNavigate();

  const authContext = useContext(AuthContext);
  const userManagementContext = useContext(UserManagementContext);

  const [password, setPassword] = useState(false);
  const [newPasswordStrength, setNewPasswordStrength] = useState(0);

  const currentUser = authContext.currentUser;

  const userUpdateSchema = yup.object({
    first_name: yup.string().required('First Name is required'),
    last_name: yup.string().required('Last Name is required'),
    password_flag: yup.bool(),
    email: yup.string().email('Email must be valid').required('Email is required'),
    current_password: yup.string().when('password_flag', {
      is: true,
      then: yup.string().required('Must enter current password'),
    }),
    new_password: yup.string().when('password_flag', {
      is: true,
      then: yup.string().required('Must enter new password'),
    }),
    confirm_password: yup
      .string()
      .oneOf([yup.ref('new_password'), null], 'Confirm password must be same as new password')
      .when('password_flag', {
        is: true,
        then: yup.string().required('Must enter confirm password'),
      }),
  });

  const form = useForm({
    resolver: yupResolver(userUpdateSchema),
  });
  const { control, handleSubmit, reset, setValue, setError } = form;

  const handleSaveData = async userformData => {
    let payload = {};
    const formData = {
      first_name: userformData.first_name,
      last_name: userformData.last_name,
      email: userformData.email,
      new_password: userformData.new_password,
      current_password: userformData.current_password,
    };
    for (const prop in formData) {
      if (formData[prop]) {
        payload[prop] = formData[prop];
      }
    }
    const res = await userManagementContext.updateCurrentUser(payload);
    if (res.errors && Object.keys(res.errors).length) {
      reset(currentUser);
      Object.keys(res.errors).forEach(key => {
        if (key === 'new password') {
          setError('new_password', {
            type: 'custom',
            message: res.errors['new password'],
          });
        } else if (key === 'old password') {
          setError('current_password', {
            type: 'custom',
            message: res.errors['old password'],
          });
        } else {
          setError(key, {
            type: 'custom',
            message: res.errors[key],
          });
        }
      })
    }
  };

  const getSpecialites = async () => {
    await userManagementContext.getAllSpecialties();
  };

  useEffect(() => {
    getSpecialites();
  }, []);

  useEffect(() => {
    reset(currentUser);
    // eslint-disable-next-line
  }, [currentUser]);

  const renderSpecialty = userManagementContext.specialties?.length
    ? userManagementContext.specialties?.map(specialist => {
      return specialist.name ? toTitleCase(specialist.name) : '';
    })
    : '';
  return (
    <>
      <CQContainer ml={0} mr={0}>
        <form onSubmit={handleSubmit(handleSaveData)}>
          <HStack m={4}>
            <Box w="xs">
              <GridItem h={'55px'}>
                <FormInputField
                  name="first_name"
                  control={control}
                  id="first_name"
                  onChange={e => {
                    setValue('first_name', e.target.value);
                  }}
                />
              </GridItem>
            </Box>
            <Box w="xs">
              <GridItem h={'55px'}>
                <FormInputField
                  name="last_name"
                  control={control}
                  id="last_name"
                  onChange={e => {
                    setValue('last_name', e.target.value);
                  }}
                />
              </GridItem>
            </Box>
          </HStack>

          <HStack m={4}>
            <Box w="xs">
              <GridItem>
                <FormInputField
                  name="email"
                  height={'50px'}
                  control={control}
                  id="email"
                  disabled={currentUser.is_superuser}
                  leftIcon={<MailIcon style={{marginTop:'10px'}} />}
                  {...currentUser.is_superuser && {rightIcon: <></>}}
                  size="md"
                  onChange={e => {
                    setValue('email', e.target.value);
                  }}
                />
              </GridItem>
            </Box>
            <Box w="xs">
              <GridItem>
                <FormTextInput
                  name="role"
                  height={'50px'}
                  control={control}
                  id="role"
                  disabled
                  leftIcon={<ViewIcon style={{marginTop:'10px'}}  />}
                  size="md"
                />
              </GridItem>
            </Box>
          </HStack>

          <HStack ml={5} alignItems={'center'}>
            <Text
              fontWeight="bold"
              fontSize="14px"
              style={{ fontFamily: FONT_FAMILY.ptSans }}
              cursor={'pointer'}
              onClick={() => {
                setPassword(!password)
                setValue('password_flag', !password)
              }}
            >
              Change Password
            </Text>
            {!password ? (
              <DownIcon
                onClick={() => {
                  setPassword(!password)
                  setValue('password_flag', !password)
                }}
                style={{ width: 10, height: 10, cursor: 'pointer' }}
              />
            ) : (
              <UpIcon
                onClick={() => {
                  setPassword(!password)
                  setValue('password_flag', !password)

                }}
                style={{ width: 10, height: 10, cursor: 'pointer' }}
              />
            )}
          </HStack>

          {password && (
            <Box mt="5">
              <HStack m={4}>
                <Box w="xs">
                  <GridItem h={'60px'}>
                    <FormPasswordInput
                      control={control}
                      style={{height:'50px'}}
                      name="current_password"
                      id="Current_password"
                      placeholder="Current Password"
                      leftIcon={<LockIcon style={{marginTop:'10px'}}  />}
                      passwordShowIcon={<EyeHiddenIcon  style={{marginTop:'10px'}} />}
                      passwordHideIcon={<EyeIcon style={{marginTop:'10px'}} />}
                      size="md"
                    />
                  </GridItem>
                </Box>
              </HStack>
              <HStack m={4} alignItems='baseline'>
                <Box w="xs">
                  <FormPasswordInput
                    control={control}  style={{height:'50px'}}
                    name="new_password"
                    id="new_password"
                    placeholder="New Password"
                    leftIcon={<LockIcon  style={{marginTop:'10px'}}  />}
                    passwordShowIcon={<EyeHiddenIcon   style={{marginTop:'10px'}} />}
                    passwordHideIcon={<EyeIcon  style={{marginTop:'10px'}}  />}
                    size="md"
                    onChange={e => {
                      const value = strengthChecker(e.target.value);
                      setNewPasswordStrength(value);
                    }}
                  />
                  <Box pt={2} />
                  <Text fontSize='sm' ml={1}>Password Strength</Text>
                  <PasswordSrength strength={newPasswordStrength} />
                </Box>

                <Box w="xs">
                  <FormPasswordInput
                    control={control}
                    name="confirm_password"  style={{height:'50px'}}
                    id="confirm_password"
                    placeholder="Confirm Password"
                    leftIcon={<LockIcon  style={{marginTop:'10px'}}  />}
                    passwordShowIcon={<EyeHiddenIcon  style={{marginTop:'10px'}}  />}
                    passwordHideIcon={<EyeIcon  style={{marginTop:'10px'}}  />}
                    size="md"
                    onChange={e => {
                      strengthChecker(e.target.value);
                    }}
                  />
                </Box>
              </HStack>
            </Box>
          )}
          <Box ml={5} mt={5}>
            <Heading
              as="h6"
              fontSize="14px"
              style={{ fontFamily: FONT_FAMILY.ptSans, fontWeight: 'bolder' }}
              mb={5}
            >
              Specialties
            </Heading>
            <Flex justifyContent={'flex-start'} flexWrap={'wrap'} pr={4} >
              {userManagementContext.isSpecialtyList ? (
                <Spinner />
              ) : renderSpecialty?.length ? (
                // renderSpecialty?.join(' | ')

                <Flex flexWrap={'wrap'} justifyContent={'start'} w={'98%'} >
                  {renderSpecialty.map(n => (
                    <Text
                      bg={AppColors.gray}
                      m={1}
                      px={5}
                      py={2}
                      borderRadius={'full'}
                    >
                      {n}
                    </Text>
                  ))}
                </Flex>
              ) : (
                ''
              )}
            </Flex>
          </Box>
          <Box mt={12} ml={5}>
            <HStack>
              <Button
                mr={1}
                // variant="outline"
                color={AppColors.secondary}
                height={'50px'}
                _hover={{
                  transition: 'all .1s ease',
                  bgColor: AppColors.secondary,
                  color: AppColors.white,
                  // outline: ,
                }}
                style={{
                  paddingInline: '35px',
                  border:`2px solid ${AppColors.secondary}`
                }}
                // outline={`2px solid ${AppColors.secondary}`}
                bg={'#fff'}
                fontSize="sm"
                borderRadius="3xl"
                onClick={() => {
                  navigate(ROUTES.ASSIGN_AUDITS_V2);
                }}
              >
                Cancel
              </Button>
              <Button
                borderRadius="3xl"
                color="white"
                outline={`2px`}
                height={'50px'}
                _hover={{
                  transition: 'all .1s ease',
                  bgColor: AppColors.white,
                  color: AppColors.primary,
                  outline: `2px solid ${AppColors.primary}`,
                }}

                fontSize="sm"
                bg={AppColors.primary}
                style={{ paddingInline: '35px' }}
                isLoading={userManagementContext.isUserLoading}
                type="submit"
              >
                Save User
              </Button>
            </HStack>
          </Box>
        </form>
      </CQContainer>
    </>
  );
};
export default AccountForm;
